'use client';

import React from 'react';
import { Award } from 'lucide-react';
import type { PublicRecord } from '@/types/publicRecords';
import AppLogo from '@/components/ui/AppLogo';
import {
  buildCertificateDocumentMeta,
  CERTIFICATE_ATTESTATION,
  CERTIFICATE_NOT_ATTESTATION,
} from '@/lib/certificateDocument';
import './certificate-print.css';

type Props = {
  record: PublicRecord;
  compact?: boolean;
};

export default function CertificateDocument({ record, compact = false }: Props) {
  const meta = buildCertificateDocumentMeta(record);

  return (
    <div
      id="certificate-print-root"
      className={`certificate-document certificate-card relative mx-auto overflow-hidden rounded-2xl bg-white ${
        compact ? 'max-w-lg' : 'max-w-3xl'
      }`}
      style={{
        border: '3px double #c9a84c',
        boxShadow: '0 24px 60px rgba(0, 43, 127, 0.12), 0 0 0 1px rgba(201, 168, 76, 0.2)',
      }}
    >
      <div
        className="h-2 w-full"
        style={{
          background: 'linear-gradient(90deg, #c9a84c 0%, #e879a9 35%, #8b5cf6 65%, #22d3ee 100%)',
        }}
      />

      <div className={`relative ${compact ? 'p-5 sm:p-6' : 'p-7 sm:p-10'}`}>
        <AppLogo
          size={compact ? 150 : 260}
          className="pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 opacity-[0.045]"
        />
        <div
          className="pointer-events-none absolute inset-4 rounded-xl opacity-[0.07]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, #002b7f 0, #002b7f 1px, transparent 0, transparent 50%)',
            backgroundSize: '12px 12px',
          }}
        />

        <header className="certificate-section relative mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-amber-200/80 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 shadow-md ring-1 ring-amber-200">
              <AppLogo size={42} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#002b7f]">
                Ertzyx Ledger
              </p>
              <p className="text-xs text-slate-500">
                Certificate of Institution-Reviewed Record · Not a government credential
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-800">
              Certificate ID
            </p>
            <p data-clarity-mask="true" className="font-mono text-xs font-bold text-[#002b7f]">
              {meta.certificateId}
            </p>
          </div>
        </header>

        <div className="certificate-section relative mb-6 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1">
            <Award size={14} className="text-amber-700" />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800">
              {meta.trustLevelLabel}
            </span>
          </div>
          <h1
            data-clarity-mask="true"
            className="mb-2 font-serif text-2xl font-bold leading-tight text-[#002b7f] sm:text-3xl"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {meta.certificateTitle}
          </h1>
          <p className="mx-auto max-w-md text-sm leading-relaxed text-slate-600">
            {CERTIFICATE_ATTESTATION}
          </p>
        </div>

        <div className="certificate-section certificate-block relative mb-6 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-amber-50/40 p-5 sm:p-6">
          <p className="mb-1 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
            Record preserved on Ertzyx
          </p>
          <h2
            data-clarity-mask="true"
            className="mb-3 text-center text-xl font-bold leading-snug text-[#002b7f] sm:text-2xl"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {meta.recordTitle}
          </h2>
          <p
            data-clarity-mask="true"
            className="mx-auto max-w-xl text-center text-sm leading-relaxed text-slate-600"
          >
            {meta.recordDescription}
          </p>
        </div>

        <div
          data-clarity-mask="true"
          className="certificate-section relative mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4"
        >
          {[
            { label: 'Record holder', value: meta.ownerName },
            { label: 'Pillar', value: meta.pillar },
            { label: 'Event date', value: meta.eventDate },
            { label: 'Location', value: meta.locationName },
            { label: 'Review status', value: meta.verificationStatus },
            { label: 'Evidence on file', value: `${meta.evidenceCount} item(s)` },
            { label: 'Proof summary ID', value: meta.proofCardId },
            { label: 'Issued on Ertzyx', value: meta.issuedDate },
            { label: 'Document reference', value: meta.integrityRef },
          ].map((cell) => (
            <div
              key={cell.label}
              className="rounded-lg border border-slate-100 bg-white/90 px-3 py-2.5"
            >
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                {cell.label}
              </p>
              <p className="mt-0.5 text-xs font-semibold leading-snug text-[#002b7f]">
                {cell.value}
              </p>
            </div>
          ))}
        </div>

        {meta.institutionReviewer && (
          <p data-clarity-mask="true" className="relative mb-4 text-center text-xs text-slate-600">
            <span className="font-semibold text-[#002b7f]">Review context: </span>
            {meta.institutionReviewer}
          </p>
        )}

        {meta.reviewStepsCompleted.length > 0 && (
          <div className="certificate-section certificate-block relative mb-6 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
              Review steps completed
            </p>
            <ul className="grid gap-1 sm:grid-cols-2">
              {meta.reviewStepsCompleted.map((step) => (
                <li key={step} className="flex items-center gap-2 text-xs text-emerald-900">
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-[9px] text-white">
                    ✓
                  </span>
                  {step}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="certificate-appendix certificate-block relative">
          <p className="certificate-appendix-title">Verification Appendix</p>
          <div className="certificate-section relative flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-amber-200/80 bg-amber-50/30 p-5 sm:flex-row sm:justify-between">
            <div className="text-center sm:text-left">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-900">
                Verify this certificate
              </p>
              <p
                data-clarity-mask="true"
                className="mt-1 max-w-xs font-mono text-[11px] leading-relaxed text-slate-600 break-all"
              >
                {meta.verifyUrl}
              </p>
              <p className="mt-2 text-[10px] text-slate-500">
                Scan the QR code or open the link to confirm this ID is on file at Ertzyx.
              </p>
            </div>
            <div className="flex flex-col items-center rounded-lg border border-white bg-white p-2 shadow-sm">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={meta.qrImageUrl}
                alt={`QR code to verify ${meta.certificateId}`}
                width={120}
                height={120}
                className="h-[120px] w-[120px]"
              />
              <p data-clarity-mask="true" className="mt-1 font-mono text-[9px] text-slate-400">
                {meta.integrityRef}
              </p>
            </div>
          </div>

          <div className="certificate-section relative mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                This document confirms
              </p>
              <ul className="space-y-1 text-xs leading-relaxed text-slate-700">
                <li>• Preservation of the named record on Ertzyx Ledger</li>
                <li>• Evidence count and institution-reviewed status at time of issue</li>
                <li>• A verifiable reference ID linked to the Ertzyx Trust Ledger</li>
              </ul>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50/80 p-3">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-amber-900">
                This document does not
              </p>
              <ul className="space-y-1 text-xs leading-relaxed text-amber-950/90">
                {CERTIFICATE_NOT_ATTESTATION.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>

          <footer className="certificate-section relative mt-6 border-t border-slate-200 pt-4 text-center">
            <p className="text-[10px] leading-relaxed text-slate-500">{meta.disclaimer}</p>
            <p className="mt-2 font-mono text-[9px] text-slate-400">
              Document type: ERTZYX_VERIFICATION_CERTIFICATE · Version 1.0
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
